import React from "react";
import DashboardCard from "../_shared/DashboardCard/DashboardCard";
import LoadingOverlay from "../_shared/LoadingOverlay/LoadingOverlay";
import FullMessageOverlay from "../_shared/FullMessageOverlay/FullMessageOverlay";
import ErrorOverlay from "../_shared/ErrorOerlay/ErrorOerlay";
import { CompanyResult } from "../../../model/dashboard.types";
import css from "./DepartmentPlanCard.module.css";
import Tooltip from "@/shared/components/organism/Tooltip/Tooltip";
import { Link } from "react-router-dom";

export function PlanSection({
  title,
  result,
  showPaid,
  forecastValue,
  plannedSalesValue,
  plannedSalesLabel,
}: {
  title: string;
  result: CompanyResult;
  showPaid?: boolean;
  forecastValue?: number | null;
  plannedSalesValue?: number | null;
  plannedSalesLabel?: string;
}) {
  const fact = result.value;
  const remaining = Math.max(result.target - fact, 0);
  const percent = result.precent;
  const isComplete = percent >= 100;

  const trendInfo =
    result.lastPeriod && result.lastPeriod.value !== 0
      ? (() => {
          const diffPercent =
            ((fact - result.lastPeriod.value) / result.lastPeriod.value) * 100;
          const isUp = diffPercent > 0;
          const abs = Math.abs(diffPercent).toFixed(1);
          const text = isUp
            ? `+${abs}%`
            : diffPercent < 0
              ? `−${abs}%`
              : "0%";
          return { text, isUp, isDown: diffPercent < 0 };
        })()
      : null;

  return (
    <div className={css.metricBlock}>
      <div className={css.metricTitleRow}>
        <div className={css.metricTitle}>{title}</div>
        <Link
          className={css.methodLink}
          to={`/SalePlan/methodology#${title === "Оборот" ? "closed-fact" : "gross-profitability"}`}
          onClick={(event) => event.stopPropagation()}
        >
          Как это считается
        </Link>
      </div>
      <div className={css.metricsRow}>
        <div className={css.metricItem}>
          <div className={css.metricLabel}>План</div>
          <div className={css.metricValue}>
            {result.target.toLocaleString()} ₽
          </div>
          {forecastValue != null && (
            <div className={css.metricForecast}>
              Прогноз отдела: {forecastValue.toLocaleString("ru-RU")} ₽
            </div>
          )}
          {plannedSalesValue != null && (
            <div
              className={
                forecastValue != null ? css.metricPlannedSales : css.metricPlannedSalesUnderPlan
              }
            >
              {plannedSalesLabel ?? "Плановые продажи"}:{" "}
              {plannedSalesValue.toLocaleString("ru-RU")} ₽
            </div>
          )}
        </div>
        <div className={css.metricItem}>
          <div className={css.metricLabel}>Факт</div>
          <div className={css.metricValue}>
            {fact.toLocaleString()} ₽
          </div>
          {result.valuePreparation != null && (
            <div className={css.metricPreparation}>
              К отгрузке: {result.valuePreparation.toLocaleString("ru-RU")} ₽
            </div>
          )}
          {result.trend != null && (
            <div className={css.metricTrend}>
              Тренд: {result.trend.value.toLocaleString("ru-RU", {
                maximumFractionDigits: 1,
              })} 
            </div>
          )}
          <div className={css.metricSub}>
            {percent.toFixed(1)}% выполнения
          </div>
        </div>
        {result.lastPeriod != null && (
          <div className={css.metricItem}>
            <Tooltip text="Факт за прошлый период">
              <div className={css.metricLabel}>Прошлый период</div>
            </Tooltip>
            <div className={css.metricValue} style={{ fontSize: "1rem" }}>
              {result.lastPeriod.value.toLocaleString()} ₽
            </div>
            {trendInfo && (
              <div
                className={`${css.metricSub} ${
                  trendInfo.isUp ? css.trendUp : trendInfo.isDown ? css.trendDown : ""
                }`}
              >
                {trendInfo.text} к прошлому периоду
              </div>
            )}
          </div>
        )}
      </div>
      <div className={css.progressWrap}>
        <div className={css.progressBar}>
          <div
            className={css.progressFill}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
        <div className={css.progressPercent}>
          {isComplete ? "Выполнено" : `Осталось: ${remaining.toLocaleString()} ₽`}
        </div>
      </div>
      {showPaid && (result.paid != null && result.paid > 0) && (
        <div className={css.footerRow}>
          <div className={css.footerItem}>
            <span>Заплачено:</span>
            <span className={css.footerValue}>
              {result.paid.toLocaleString()} ₽
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

type DepartmentPlanCardProps = {
  title: string;
  col: number;
  row: number;
  loading: boolean;
  error: string | null;
  opportunity: CompanyResult | null;
  profit: CompanyResult | null;
  opportunityForecast?: number | null;
  profitForecast?: number | null;
  opportunityPlannedSales?: number | null;
  profitPlannedSales?: number | null;
  onClick?: () => void;
};

function DepartmentPlanCard({
  title,
  col,
  row,
  loading,
  error,
  opportunity,
  profit,
  opportunityForecast,
  profitForecast,
  opportunityPlannedSales,
  profitPlannedSales,
  onClick,
}: DepartmentPlanCardProps) {
  const hasOpportunity =
    opportunity != null && (opportunity.target ?? 0) > 0;
  const hasProfit =
    profit != null && (profit.target ?? 0) > 0;

  return (
    <DashboardCard
      col={col}
      height={row * 50}
      rowSpan={row}
      title={title}
      isHover
      onClick={onClick}
    >
      {loading ? (
        <LoadingOverlay loading />
      ) : error ? (
        <ErrorOverlay error={error} />
      ) : !hasOpportunity && !hasProfit ? (
        <FullMessageOverlay fullMessage="Нет данных" />
      ) : (
        <div className={css.card}>
          {hasOpportunity && (
            <PlanSection
              title="Оборот"
              result={opportunity!}
              showPaid
              forecastValue={opportunityForecast}
              plannedSalesValue={
                opportunityPlannedSales ?? opportunity?.plannedSalesOpportunity ?? null
              }
              plannedSalesLabel="Плановые продажи, сумма"
            />
          )}
          {hasProfit && (
            <PlanSection
              title="Валовая прибыль"
              result={profit!}
              forecastValue={profitForecast}
              plannedSalesValue={profitPlannedSales ?? profit?.plannedSalesProfit ?? null}
              plannedSalesLabel="Плановые продажи, прибыль"
            />
          )}
        </div>
      )}
    </DashboardCard>
  );
}

export default DepartmentPlanCard;
